import React, { useEffect } from "react";
import { BrowserRouter, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AuthUtils } from "@tecma/auth-utils";
import i18n from "@tecma/i18n";

import { Scaffold } from "components/Scaffold";
import PageLoader from "components/PageLoader";
import { NewUserModal } from "components/NewUserModal/NewUserModal";
import { SwiperProvider } from "components/SwiperContext/SwiperContext";

import { authActions } from "store/sagas/auth/auth.actions";
import { authStateSelectors } from "store/slices/auth/selectors";
import { applicationStateSelectors } from "store/slices/applicationState/selectors";
import { applicationInitActions } from "store/sagas/application/init/init.actions";
import { applicationStateActions } from "store/slices/applicationState/slice";
import { environmentSelectors } from "store/slices/environment/selector";
import { EnvironmentState } from "store/slices/environment/slice";
import useDeviceDimension from "hooks/useDeviceDimension";
import { getFavicon } from "utils/getFavicon";
import useSurveyInfo from "hooks/useSurveyInfo";
import { surveysActions } from "modules/survey/infrastructure/store/saga/survey.actions";
import { Routes } from "./routes";
// styles
import "./theme/global.scss";

const AppContent: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    sendUnloggedSurveysResponses,
    hasCompletedAllLoggedSurveys,
    roomSelectorUserSurvey,
  } = useSurveyInfo();
  const appIsLoading = useSelector(applicationStateSelectors.selectIsLoading);

  const isAuthenticated = useSelector(authStateSelectors.userIsLogged);
  const loggedUser = useSelector(authStateSelectors.userLogged);
  const token = useSelector(authStateSelectors.token);
  const projectId = useSelector(applicationStateSelectors.projectID);
  const projectInfo = useSelector(applicationStateSelectors.selectProjectInfo);
  const myHome = useSelector(applicationStateSelectors.myHome);
  const showNewUserModal = useSelector(
    applicationStateSelectors.showNewUserModal,
  );
  const environment = useSelector(
    environmentSelectors.environment,
  ) as EnvironmentState;
  const roomSelectorConfiguration = useSelector(
    applicationStateSelectors.roomSelectorConfiguration,
  );

  useDeviceDimension();

  useEffect(() => {
    if (!projectInfo?.displayName) return;
    getFavicon({
      displayName: projectInfo?.displayName,
      bucketBaseUrl: environment.REACT_APP_BUCKET_BASEURL,
    });
  }, [projectInfo?.displayName, environment.REACT_APP_BUCKET_BASEURL]);

  const handleCloseNewUserModal = () => {
    dispatch(applicationStateActions.showNewUserModal(false));
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.has("clientId")) {
      searchParams.delete("clientId");
      const newSearch = searchParams.toString();
      const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ""}`;
      navigate(newUrl, { replace: true });
    }
  };

  const handleRedirectToMyHome = () => {
    AuthUtils.redirectToTool({
      redirectUrl: myHome?.url ?? "",
      projectId: projectId ?? "",
      locale: i18n.language,
    });
    dispatch(applicationStateActions.showNewUserModal(false));
  };

  useEffect(() => {
    dispatch(applicationInitActions.initApplicationRequested());
  }, [dispatch]);

  useEffect(() => {
    if (isAuthenticated && loggedUser && token) {
      dispatch(authActions.startCountDown({ token, user: loggedUser }));
    }
  }, [isAuthenticated, dispatch, loggedUser, token]);

  useEffect(() => {
    if (projectId && roomSelectorConfiguration?.featureFlags.surveyModule) {
      dispatch(surveysActions.getSurveysByProjectRequested(projectId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, projectId, roomSelectorConfiguration]);

  useEffect(() => {
    if (isAuthenticated && loggedUser && roomSelectorUserSurvey) {
      roomSelectorUserSurvey.forEach((survey) => {
        localStorage.setItem(
          `surveyResponses-${survey.survey_id}`,
          JSON.stringify({
            questionsStatus: survey.questionsStatus,
            isCompleted: survey.isCompleted,
            surveyOrder: survey.surveyOrder,
          }),
        );
      });
    }
  }, [
    dispatch,
    isAuthenticated,
    loggedUser,
    projectId,
    roomSelectorUserSurvey,
  ]);

  useEffect(() => {
    if (isAuthenticated && !hasCompletedAllLoggedSurveys) {
      sendUnloggedSurveysResponses();
    }
  }, [
    isAuthenticated,
    sendUnloggedSurveysResponses,
    hasCompletedAllLoggedSurveys,
  ]);

  if (appIsLoading || !projectInfo) {
    return <PageLoader />;
  }

  return (
    <Scaffold>
      {showNewUserModal && (
        <NewUserModal
          isOpen={showNewUserModal}
          onClose={handleCloseNewUserModal}
          onRedirectToMyHome={handleRedirectToMyHome}
        />
      )}
      <SwiperProvider>
        <Routes />
      </SwiperProvider>
    </Scaffold>
  );
};

const App: React.FC = () => {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
};

export default App;
